import { Flex, Typography } from 'antd';
import dayjs from 'dayjs';
import { type ReactNode, useContext, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import CustomerOrderForm from './customer-order.form';
import LoadingSpin from '../../../../components/loading-spin';
import SyncSuspense from '../../../../components/sync-suspense';
import { AuthContext } from '../../../../contexts/auth-context-provider';
import { useCartContext } from '../../../../contexts/cart.context';
import { Customer, CustomerOrder } from '../../../../entities';
import useCustomerOptions from '../../../../hooks/use-customer-options';
import useErrorNotification from '../../../../hooks/use-error-notification';
import useWarnBeforeUnload from '../../../../hooks/use-warn-before-unload';
import utils from '../../../../utils/utils';

const timeZone = process.env.REACT_APP_TIMEZONE;

const CustomerOrderFormPage = (): ReactNode => {
  const { session } = useContext(AuthContext);
  const { customerOrder: cartOrder, clearCart } = useCartContext();
  const [searchParams] = useSearchParams();
  const customers = useCustomerOptions();
  useErrorNotification(customers.errorMessage);
  useWarnBeforeUnload();

  // Worked out once, when the customers are loaded: the order kept in the cart while its customer
  // can still order for its date, else a new order
  const newOrder = useMemo(() => {
    if (!customers.data) return undefined;
    const findCustomer = (id: string | undefined): Customer | undefined =>
      customers.data?.find((customer) => customer.id === id);

    const cartCustomer = findCustomer(cartOrder?.customer?.id);
    const earliestValidOrderDate = utils.addBusinessDays(
      dayjs().tz(timeZone),
      cartCustomer?.customerSetting.dueDaysToPlaceOrders ?? 2,
      cartCustomer?.customerSetting.dueTimeToPlaceOrders ?? '12:00'
    );
    const isFromCart = !!cartOrder?.orderDate.isAfter(earliestValidOrderDate);
    if (isFromCart) {
      return { customerOrder: Object.assign(new CustomerOrder(), cartOrder), isFromCart };
    }
    // The customer asked for in the URL, else the customer user's own customer
    const customer =
      findCustomer(searchParams.get('preSelectedCustomerId') ?? undefined) ??
      findCustomer(String(session?.claims.customerId)) ??
      new Customer();
    return {
      customerOrder: Object.assign(new CustomerOrder(), { orderDate: undefined, customer }),
      isFromCart,
    };
  }, [customers.data]);

  useEffect(() => {
    // Clear the cart if the order in the cart is no longer valid (e.g., the order date is no longer valid)
    if (newOrder && !newOrder.isFromCart) clearCart();
  }, [newOrder]);

  return (
    <Flex vertical>
      <Typography.Title level={2}>New order</Typography.Title>
      <SyncSuspense fallback={<LoadingSpin />} condition={newOrder}>
        {newOrder && (
          <CustomerOrderForm
            customerOrder={newOrder.customerOrder}
            customers={customers.data ?? []}
          />
        )}
      </SyncSuspense>
    </Flex>
  );
};

export default CustomerOrderFormPage;
